import { useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";
import { useT } from "@/i18n";

// Language-aware promo videos. Each MP4 was recorded natively in the target
// language so we don't overlay any translations — the file itself IS the
// localization. Missing languages fall back to English so a French visitor
// (until the French cut is uploaded) still gets a working video.
const LOCALIZED_VIDEOS = {
  tr: "https://customer-assets-0z36b82j.emergentagent.net/job_ai-webpage-gen/artifacts/txeartrv_turkce.mp4",
  en: "https://customer-assets-0z36b82j.emergentagent.net/job_ai-webpage-gen/artifacts/mweb14v5_ingilizce.mp4",
  es: "https://customer-assets-0z36b82j.emergentagent.net/job_ai-webpage-gen/artifacts/mlmz8gcr_ispanyolca.mp4",
  de: "https://customer-assets-0z36b82j.emergentagent.net/job_ai-webpage-gen/artifacts/6tyvu0cw_almanca.mp4",
  it: "https://customer-assets-0z36b82j.emergentagent.net/job_ai-webpage-gen/artifacts/16diwsek_italyanca.mp4",
  fr: "https://customer-assets-0z36b82j.emergentagent.net/job_ai-webpage-gen/artifacts/n9i3yqly_fransizca.mp4",
};

function resolveVideo(lang) {
  return LOCALIZED_VIDEOS[lang] || LOCALIZED_VIDEOS.en;
}

/**
 * VideoDemo — plays a language-specific brand promo. Switches automatically
 * whenever the user changes the site language.
 *  - Muted autoplay when the section scrolls into view (respects Chrome
 *    autoplay policy: only muted playback is allowed without a gesture).
 *  - Center Play overlay when paused.
 *  - Mute/Unmute toggle bottom-right.
 *  - Language change → new <source> URL → we call load() so the browser
 *    fetches and plays the correct cut, preserving the user's play/mute
 *    preference.
 */
export default function VideoDemo() {
  const { t, lang } = useT();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const src = resolveVideo(lang);

  // Autoplay (muted) when the section enters viewport.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  // Reload the video whenever the language changes so the new src actually
  // takes effect. React's <video> element won't re-fetch just because `src`
  // changed on the JSX — we have to call .load() explicitly.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => {});
  }, [src]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
    } else {
      v.pause();
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      className="border-b border-amber-100 bg-gradient-to-b from-[#FFFBF5] to-white"
      data-testid="video-demo-section"
    >
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">
            {t("demo_eyebrow")}
          </div>
          <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter">
            {t("demo_h2")}
          </h2>
          <p className="mt-4 text-lg text-zinc-600">{t("demo_sub")}</p>
        </div>

        <div className="mt-12 max-w-4xl mx-auto">
          <div
            className="relative rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10 border border-amber-200 bg-black aspect-video"
            data-testid="video-demo-player"
          >
            <video
              ref={videoRef}
              key={src}
              src={src}
              className="w-full h-full object-cover"
              muted={muted}
              loop
              playsInline
              autoPlay
              preload="metadata"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onClick={togglePlay}
              data-testid={`video-demo-lang-${lang}`}
            />

            {/* Center play button (only when paused) */}
            {!playing && (
              <button
                onClick={togglePlay}
                data-testid="video-play-btn"
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                aria-label="Play video"
              >
                <div className="w-20 h-20 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-2xl">
                  <Play className="w-8 h-8 text-orange-600 ml-1" fill="currentColor" />
                </div>
              </button>
            )}

            {/* Mute toggle */}
            <button
              onClick={toggleMute}
              data-testid="video-mute-btn"
              className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>

            {/* Live badge */}
            <div className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-widest text-white bg-emerald-500/90 px-2 py-1 rounded-full flex items-center gap-1.5 shadow-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Demo · {lang.toUpperCase()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
